// Stencil
import { Component, Host, h, Prop, Element, State, Watch } from "@stencil/core";
// Other Libraries
import { MercuryBundles, getIconPath } from "@genexus/mercury";
import {
  ActionListItemModel,
  ActionListModel
} from "@genexus/chameleon-controls-library";
import { ActionListItemGroup } from "@genexus/chameleon-controls-library/dist/types/components/action-list/types";
// Gx Ide Ui
import { Locale } from "../../common/locale";
import { mapCategoryToListItemGroup, mapDataTypesToList } from "./helpers";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/list-box",
  "utils/form--full",
  "utils/typography",
  "utils/layout",
  "utils/spacing",
  "chameleon/scrollbar"
];

const ARROW_UP_KEY = "ArrowUp";
const ARROW_DOWN_KEY = "ArrowDown";
const ENTER_KEY = "Enter";
const TAB_KEY = "Tab";

const ALL_CATEGORY_ID = "all";
const RECENTLY_USED_CATEGORY_ID = "recenttly-used";

const CLOCK_ICON = getIconPath({
  category: "system",
  name: "time",
  colorType: "primary"
});

@Component({
  tag: "gx-ide-data-type-selector",
  styleUrl: "data-type-selector.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/data-type-selector"]
})
export class GxIdeKbManagerImport {
  #_componentLocale: any;
  #showAll: boolean = false;
  #categoriesActionListItemGroupMap: Map<string, ActionListItemGroup> = new Map<
    string,
    ActionListItemGroup
  >();

  #dataTypeCategoryMap: Map<string, string> = new Map<string, string>();
  #dataTypeDataIdMap: Map<string, DataTypeData> = new Map<
    string,
    DataTypeData
  >();
  #lastArrowKey: typeof ARROW_UP_KEY | typeof ARROW_DOWN_KEY | null = null;
  #selectedDataTypeIds: SelectedDataTypeIds = {
    // For setting focus
    categoryId: undefined,
    dataTypeId: undefined
  };
  #focusAlreadySet: boolean = false;

  #actionListEl!: HTMLChActionListRenderElement;
  @Element() el: HTMLGxIdeDataTypeSelectorElement;

  @State() categoriesListBoxModel: ActionListItemModel[] = [];
  @State() selectedCategory: string;
  @State() recentlyUsedActionListModel: ActionListModel = [];
  @State() loading: boolean = true;
  @State() categoriesJustChanged: boolean = false;
  @State() actionListDidRender: boolean = false;

  /**
   * All possible types and its category
   */
  @Prop() readonly categories: DataTypeCategoryData[] = undefined;
  @Watch("categories")
  categoriesChanged(newCategories: DataTypeCategoryData[]) {
    if (!newCategories?.length) {
      return;
    }

    this.#categoriesActionListItemGroupMap.clear();
    newCategories.forEach(category => {
      const categoryListBoxModel = mapCategoryToListItemGroup(category);
      this.#categoriesActionListItemGroupMap.set(
        category.name,
        categoryListBoxModel
      );
      category.types.forEach(type => {
        const isSelected = type.selected;
        if (isSelected) {
          // For setting focus
          this.#selectedDataTypeIds.categoryId = category.name;
          this.#selectedDataTypeIds.dataTypeId = type.name;
        }

        this.#dataTypeCategoryMap.set(type.name, category.id);
        this.#dataTypeDataIdMap.set(type.name, {
          id: category.id,
          name: type.name,
          icon: type.icon,
          selected: type.selected
        });
      });
    });
    this.loading = false;
    this.#showAllObjects();
    this.categoriesJustChanged = true;
  }

  /**
   * Callback invoked when the user has selected an item type
   */
  @Prop() readonly dataTypeSelectedCallback: DataTypeSelectedCallback;

  /**
   * Defines the type of focus applied when the component renders for the first time.
   */
  @Prop() readonly arrowUpOnFirstElement: ArrowFirstOnFirstElement;

  /**
   * Defines the type of focus applied when the component renders for the first time.
   */
  @Prop() readonly focusType: "first-element" | "selected" = "first-element";

  /**
   * All recently used types list
   */
  @Prop() readonly recentlyUsed: DataTypeData[] = [];
  @Watch("recentlyUsed")
  recentlyUsedChanged(newRecentlyUsed: DataTypeData[]) {
    this.recentlyUsedActionListModel = null;
    this.recentlyUsedActionListModel = mapDataTypesToList(newRecentlyUsed);
  }

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);
    // initialize models
    this.recentlyUsed.length && this.recentlyUsedChanged(this.recentlyUsed);
  }

  #evaluateSelectionChanged = (path: EventTarget[]) => {
    const chActionListItem = path.find(
      el => (el as HTMLElement).tagName === "CH-ACTION-LIST-ITEM"
    );
    if (chActionListItem) {
      const id = (chActionListItem as HTMLElement).id;
      const dataTypeData = this.#dataTypeDataIdMap.get(id);
      this.dataTypeSelectedCallback(dataTypeData);
    }
  };

  #listKeyDownHandler = (event: KeyboardEvent) => {
    const pressedKey = event.key;
    const path = event.composedPath();

    if (pressedKey === ARROW_UP_KEY || pressedKey === ARROW_DOWN_KEY) {
      this.#lastArrowKey = pressedKey;

      if (pressedKey === ARROW_UP_KEY) {
        // Check if this is the first group. IF it is, let the host know,
        // so it can close the dialog.

        const chActionListItem = path.find(
          el => (el as HTMLElement).tagName === "CH-ACTION-LIST-ITEM"
        );

        if (chActionListItem !== undefined) {
          return;
        }

        // This is a ch-action-list-group
        const actionGroup = path.find(
          el => (el as HTMLElement).tagName === "CH-ACTION-LIST-GROUP"
        );
        const currentFocusedActionGroupId = (actionGroup as HTMLElement).id;

        const firstActionListGroupId =
          this.#actionListEl.shadowRoot.firstElementChild.id;

        if (currentFocusedActionGroupId === firstActionListGroupId) {
          this.arrowUpOnFirstElement();
        }
      }
    } else if (pressedKey === ENTER_KEY) {
      this.#evaluateSelectionChanged(path);
    } else if (pressedKey === TAB_KEY) {
      event.stopPropagation();
    }
  };

  #listClickHandler = (event: MouseEvent) => {
    if (this.#lastArrowKey) {
      // User navigated with ArrowUp or ArrowDown
      // Ignore, since this was not a click event.
    } else if (event.detail === 0) {
      // ignore keyboard Enter (Enter happens by default on buttons on onClick)
      return;
    } else {
      const path = event.composedPath();
      this.#evaluateSelectionChanged(path);
    }
    this.#lastArrowKey = null;
  };

  #showAllObjects = () => {
    if (!this.#showAll) {
      const allCategoriesActionListModel: ActionListItemModel[] = [];
      let counter = 1;
      const mapLength = this.#categoriesActionListItemGroupMap.size;
      for (const key of this.#categoriesActionListItemGroupMap.keys()) {
        allCategoriesActionListModel.push(
          this.#categoriesActionListItemGroupMap.get(key)
        );
        if (counter !== mapLength) {
          allCategoriesActionListModel.push({ type: "separator" });
        }
        counter++;
      }
      this.categoriesListBoxModel = allCategoriesActionListModel;
      this.#showAll = true;
      this.selectedCategory = ALL_CATEGORY_ID;
    }
  };

  #showRecentlyObjects = () => {
    this.#showAll = false;
    this.categoriesListBoxModel = this.recentlyUsedActionListModel;
    this.selectedCategory = RECENTLY_USED_CATEGORY_ID;
  };

  private selectedCategoryHandler = (e: MouseEvent) => {
    this.selectedCategory = (e.currentTarget as HTMLButtonElement).dataset.name;
    const newSelectedCategory = [
      this.#categoriesActionListItemGroupMap.get(this.selectedCategory)
    ];
    this.categoriesListBoxModel = newSelectedCategory;
    this.#showAll = false;
  };

  #handleButtonTab = (event: KeyboardEvent) => {
    // Prevents bubbling, which causes this component to close, due to how the IDE handles Tab event
    // in a parent element.
    if (event.key === TAB_KEY || event.key === ENTER_KEY) {
      event.stopPropagation();
    }
  };

  #renderFooter = () => {
    const recentlyUsedBtnClass =
      this.selectedCategory === RECENTLY_USED_CATEGORY_ID
        ? "button-secondary"
        : "button-tertiary";
    const allBtnClass =
      this.selectedCategory === ALL_CATEGORY_ID
        ? "button-secondary"
        : "button-tertiary";
    let selectedCategoryDescription = this.selectedCategory;
    if (this.selectedCategory === RECENTLY_USED_CATEGORY_ID) {
      selectedCategoryDescription =
        this.#_componentLocale.fixedTypes.recentlyUsed;
    } else if (this.selectedCategory === ALL_CATEGORY_ID) {
      selectedCategoryDescription = this.#_componentLocale.fixedTypes.all;
    } else {
      selectedCategoryDescription = this.selectedCategory;
    }

    return (
      <footer
        class="footer dialog-footer dialog-footer-start scrollable
      "
      >
        <div class="buttons-spacer">
          {this.recentlyUsed && this.recentlyUsed.length > 0 && (
            <button
              class={`button-icon-only ${recentlyUsedBtnClass}`}
              aria-label={`${this.#_componentLocale.select} ${
                this.#_componentLocale.fixedTypes.recentlyUsed
              }`}
              title={`${this.#_componentLocale.select} ${
                this.#_componentLocale.fixedTypes.recentlyUsed
              }`}
              onClick={this.#showRecentlyObjects}
              onKeyDown={this.#handleButtonTab}
            >
              <ch-image class="icon-m" src={CLOCK_ICON}></ch-image>
            </button>
          )}
          <button
            class={`button-icon-only ${allBtnClass}`}
            onClick={this.#showAllObjects}
            onKeyDown={this.#handleButtonTab}
            aria-label={`${this.#_componentLocale.select} ${
              this.#_componentLocale.fixedTypes.all
            }`}
            title={`${this.#_componentLocale.select} ${
              this.#_componentLocale.fixedTypes.all
            }`}
          >
            <span class="pseudo-icon">[*]</span>
          </button>
          {this.categories.map(category => {
            const buttonClass =
              category.name === this.selectedCategory
                ? "button-secondary"
                : "button-tertiary";

            return (
              <button
                class={`button-icon-only ${buttonClass}`}
                aria-label={category.name}
                title={category.name}
                onClick={this.selectedCategoryHandler}
                onKeyDown={this.#handleButtonTab}
                data-name={category.name}
              >
                <ch-image class="icon-m" src={category.icon}></ch-image>
              </button>
            );
          })}
        </div>
        <p class="body-regular-s selected-category">
          {selectedCategoryDescription}
        </p>
      </footer>
    );
  };

  #setFocus = async () => {
    if (this.#focusAlreadySet) {
      return;
    }
    let focusableElement: HTMLElement;

    setTimeout(() => {
      // setTimeout as a WA, since the list-box elements are not rendered/read immediatelly
      if (
        this.focusType === "selected" &&
        this.#selectedDataTypeIds.categoryId &&
        this.#selectedDataTypeIds.dataTypeId
      ) {
        // set focus on the selected element
        const selectedDataTypeEl = this.#actionListEl.shadowRoot.getElementById(
          this.#selectedDataTypeIds.dataTypeId
        );
        if (selectedDataTypeEl) {
          focusableElement = selectedDataTypeEl;
        }
      } else {
        // set focus of the first element
        const firstActionListGroup =
          this.#actionListEl.shadowRoot.firstElementChild;

        const firstButton = firstActionListGroup.shadowRoot.firstElementChild;
        focusableElement = firstButton as HTMLButtonElement;
      }

      focusableElement && (focusableElement as HTMLElement).focus();
      this.#focusAlreadySet = true;
    }, 150);
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        {this.loading ? (
          <gx-ide-loader
            loaderTitle={this.#_componentLocale.loader.title}
            description={this.#_componentLocale.loader.description}
            show
          ></gx-ide-loader>
        ) : (
          <section class="main">
            <ch-action-list-render
              ref={async (el: HTMLChActionListRenderElement) => {
                this.#actionListEl = el as HTMLChActionListRenderElement;
                this.#setFocus();
              }}
              class="list-box"
              model={this.categoriesListBoxModel}
              selection="single"
              onKeyDown={this.#listKeyDownHandler}
              onClick={this.#listClickHandler}
            ></ch-action-list-render>
            {this.#renderFooter()}
          </section>
        )}
      </Host>
    );
  }
}

export type DataTypeCategoryData = {
  id: string;
  name: string;
  icon: string;
  types: DataTypeData[];
};
export type DataTypeData = {
  id: string;
  name: string;
  icon: string;
  selected: boolean;
};

export type DataTypeSelectedCallback = (data: DataTypeData) => Promise<void>;

type SelectedDataTypeIds = {
  categoryId: string;
  dataTypeId: string;
};

export type ArrowFirstOnFirstElement = () => Promise<void>;
